/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import config from '../../../../../share/config';
import session from '../../../../../share/session';
import exam from '../../../../../share/util/inputValidator';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',//DID入力指示
  INPUT: 'input',//DID入力
  SUCCESS: 'success',//DIDデータ表示
  DID_FAILED: 'didFailed',//DIDエラー表示
  FAILED: 'failed'//共通エラー
};

export default {
  name: 'requirementSpABG002',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      inputData: '',//入力値
      dataId: '0',//確定入力値
      didData: '',//DIDデータ
      isAlert: {},
      modal_special_requirement_ABG2: false
    };
  },

  props: {
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.DID_FAILED:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_special_requirement_ABG2 = true;
      this.clearAlert();
    },

    /**
     * 終了処理
     */
    async exitProcess() {
      // Wizardを閉じる
      this.closeWizard();
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ABG2 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /** 入力画面表示 */
    async showInput(){
      this.inputData = this.dataId;
      this.phase = PHASE.INPUT;
    },

    /** データ読み出し処理 */
    async readDIDData() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // データ読み出し(要求)（バックエンドAPI_POST） Getのポーリング処理も行う
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
            value: this.dataId,
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：読み出しデータ表示
              this.didData = response.data.user_data.value;
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.phase = PHASE.DID_FAILED;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    /** データID クリア*/
    async inputClearProcess() {
      //テキストボックスクリア
      this.inputData = "";
    },
    
    /** データID入力確定*/
    async inputOkProcess() {
      if (!this.validate()) return;
      this.dataId = this.inputData;
      this.phase = PHASE.START;
    },
    
    /** DID入力指示へ戻る*/
    async returnStartProcess() {
      // 検証結果キャッシュの初期化
      this.clearAlert();
      this.inputData = "";
      this.phase = PHASE.START;
    },

    /** バリデーションチェック */
    validate: function () {
      // 検証結果キャッシュの初期化
      this.clearAlert();
      const resultPool = [];

      // データIDチェック
      this.isAlert.id = exam.validateCommons(this.inputData, true, 4, /^[A-F0-9]*$/);
      resultPool.push(this.isAlert.id.valid);

      const faults = resultPool.filter(el => el === false);
      return faults.length === 0;
    },

    clearAlert: function () {
      const alertData = {
        id: { valid: null, msg: '' }
      };

      this.isAlert = alertData;
    },
  },

  async mounted() {
    this.init();
  },
};